import { useIntl } from 'react-intl';
import { AxiosError } from 'axios';
import { ApiError } from '@/lib/types';
import { ErrorTypes } from '@/constants/ErrorTypes';

type ErrorMessageConfig = {
  id: string;
  defaultMessage: string;
};

type ErrorMessageMap = Record<
  string,
  {
    [errorCode: string]: ErrorMessageConfig;
  }
>;

type UseApiErrorHandlerOptions = {
  errorMessages?: ErrorMessageMap;
};

const useApiErrorHandler = ({
  errorMessages,
}: UseApiErrorHandlerOptions = {}) => {
  const intl = useIntl();

  const handleError = (error: AxiosError<ApiError>) => {
    const generalErrors: string[] = [];
    const fieldErrors: Record<string, string> = {};
    const unhandledErrors: string[] = [];

    const apiError = error.response?.data;
    if (!apiError) {
      return { generalErrors, fieldErrors, unhandledErrors };
    }

    const { code, message, details } = apiError.error;
    const parsedCode = code.toLowerCase();

    if (parsedCode !== 'validation_error') {
      const errorConfig =
        errorMessages?.general?.[parsedCode] ||
        ErrorTypes[parsedCode];
      if (errorConfig) {
        const translatedMessage = intl.formatMessage({
          id: errorConfig.id,
          defaultMessage: errorConfig.defaultMessage,
        });
        generalErrors.push(translatedMessage);
      } else {
        const reason = details
          ?.map((detail) => detail.reason)
          .join(', ');
        unhandledErrors.push(reason || message);
      }
      return { generalErrors, fieldErrors, unhandledErrors };
    } else {
      if (!details) {
        return { generalErrors, fieldErrors, unhandledErrors };
      }

      for (const detail of details) {
        if (
          typeof detail === 'object' &&
          detail !== null &&
          'field' in detail &&
          'reason' in detail &&
          'value' in detail
        ) {
          const validationDetail = detail as {
            field: string;
            reason: string;
            value: string;
          };
          const { field, reason } = validationDetail;

          if (field && field !== 'body' && reason) {
            fieldErrors[field] = reason;
          } else if (reason) {
            unhandledErrors.push(reason);
          }
        } else {
          unhandledErrors.push(message);
        }
      }

      return { generalErrors, fieldErrors, unhandledErrors };
    }
  };

  return { handleError };
};

export default useApiErrorHandler;
export type {
  ErrorMessageConfig,
  ErrorMessageMap,
  UseApiErrorHandlerOptions,
};
